/**
 * Shared implementation for `doodBlockConfig` and `doodModalConfig`.
 *
 * Keeps descriptor generation, CB2 projection, dashboard layout, and config
 * composition in one place so block and modal factories stay in sync.
 */

import type {
  BlocAllowedIn,
  BlocProperty,
  BlocPropertyGroup,
  CasesValue,
  ParamRef,
  SerializableValue,
  SlotDefinition,
} from './bloc-definition';
import type { BlocCategory } from './bloc-categories';
import type {
  Cb2PropertyTypeFor,
  DoodBlockField,
  DoodFieldType,
  IBlockDescriptor,
  LegacyBlockField,
} from './descriptor';
import {
  type BlockCondition,
  type ConditionalPropertyValue,
  type DescriptorControlField,
  type PropertyValue,
  DESCRIPTOR_CONTROL_FIELD_BRAND,
  PROPERTY_VALUE_BRAND,
  paletteColorValueToParamRef,
} from './property-value';
import {
  backfillLegacyFieldDefault,
  legacyAllowedRoles,
} from './legacy-field-projection';
import { lastSegment, resolveLegacyFieldHandle, toSnakeHandle } from './paths';
import type { DotPath } from './paths';
import type { TranslationField } from './translation-field';
import { expandSpacingBoxToGroupedFields } from './spacing-box';
import { markComposeAliasCanonical } from './compose-alias';
import type { DoodBlockScenarioMatrix } from './scenario-matrix';
import { definitionSetupScenarios } from './definition-setup-scenarios';

/* -------------------------------------------------------------------------- */
/* Shared field declarations                                                  */
/* -------------------------------------------------------------------------- */

export type SharedFieldDefinition = {
  name: string;
  type: DoodFieldType;
};

export type SharedFieldsDefinition = {
  props?: readonly SharedFieldDefinition[];
  descriptorControlFields?: readonly SharedFieldDefinition[];
};

type MapAccessors<List extends readonly SharedFieldDefinition[]> = {
  [Index in keyof List]: List[Index] extends SharedFieldDefinition
    ? import('./paths').PathToNested<List[Index]['name'], List[Index]['type']>
    : never;
};

type MapRequiredProps<List extends readonly SharedFieldDefinition[]> = {
  [Index in keyof List]: List[Index] extends SharedFieldDefinition
    ? import('./paths').PathToNested<List[Index]['name'], PropertyValue<List[Index]['type']>>
    : never;
};

type MapRequiredControls<List extends readonly SharedFieldDefinition[]> = {
  [Index in keyof List]: List[Index] extends SharedFieldDefinition
    ? import('./paths').PathToNested<
        List[Index]['name'],
        DescriptorControlField<List[Index]['type']>
      >
    : never;
};

export type SharedFieldAccessorsFor<
  List extends readonly SharedFieldDefinition[] | undefined,
> = List extends readonly SharedFieldDefinition[]
  ? import('./paths').UnionToIntersection<MapAccessors<List>[number]>
  : {};

export type SharedFieldAccessors<SharedFields extends SharedFieldsDefinition> = {
  props: SharedFieldAccessorsFor<SharedFields['props']>;
  descriptorControlFields: SharedFieldAccessorsFor<
    SharedFields['descriptorControlFields']
  >;
};

export type RequiredPropValues<SharedFields extends SharedFieldsDefinition> =
  SharedFields['props'] extends readonly SharedFieldDefinition[]
    ? import('./paths').UnionToIntersection<MapRequiredProps<SharedFields['props']>[number]>
    : {};

export type RequiredDescriptorControlValues<
  SharedFields extends SharedFieldsDefinition,
> = SharedFields['descriptorControlFields'] extends readonly SharedFieldDefinition[]
  ? import('./paths').UnionToIntersection<
      MapRequiredControls<SharedFields['descriptorControlFields']>[number]
    >
  : {};

/* -------------------------------------------------------------------------- */
/* Config node types                                                          */
/* -------------------------------------------------------------------------- */

export type ConfigNode =
  | PropertyValue<DoodFieldType>
  | DescriptorControlField<DoodFieldType>
  | { [key: string]: ConfigNode };

export type ResolvedConfig<Config> = {
  [K in keyof Config]: Config[K] extends PropertyValue<infer Type>
    ? import('./descriptor').FieldValueByType<Type>
    : Config[K] extends DescriptorControlField<any>
      ? never
      : Config[K] extends { [key: string]: unknown }
        ? ResolvedConfig<Config[K]>
        : Config[K];
};

export type OverrideConfig<Config> = {
  [K in keyof Config]?: Config[K] extends PropertyValue<infer Type>
    ? import('./descriptor').FieldValueByType<Type> | undefined
    : Config[K] extends DescriptorControlField<any>
      ? never
      : Config[K] extends { [key: string]: unknown }
        ? OverrideConfig<Config[K]>
        : Config[K];
};

/* -------------------------------------------------------------------------- */
/* Dashboard layout                                                           */
/* -------------------------------------------------------------------------- */

export type DashboardLayout<Config = unknown> = {
  omitFields?: readonly string[];
  tabs?: readonly DashboardTab<Config>[];
};

export type DashboardLayoutDisplayAll = 'displayAll';
export const displayAllDashboardLayout: DashboardLayoutDisplayAll = 'displayAll';

export type DashboardLayoutFactoryInput<Config = unknown> =
  | DashboardLayoutDisplayAll
  | DashboardLayoutOmitOnly<Config>
  | DashboardLayoutExplicit<Config>;

export type DashboardLayoutOmitOnly<Config = unknown> = {
  omitFields?: readonly (DotPath<Config> | (string & {}))[];
  tabs?: never;
};

export type DashboardLayoutExplicit<Config = unknown> = {
  omitFields?: readonly (DotPath<Config> | (string & {}))[];
  tabs: readonly DashboardTab<Config>[];
};

export type DashboardTab<Config = unknown> = {
  label: string;
  /** Stable tab id used as `activeTab` in scenario factories (defaults to snake_case label). */
  key?: string;
  fieldsets: readonly DashboardFieldset<Config>[];
};

export type DashboardFieldset<Config = unknown> = {
  legend: string;
  fields: readonly DashboardLayoutItem<Config>[];
};

/**
 * Declarative visibility conditions for the CB2 inspector.
 * Conditions are ANDed by default; `anyOf` provides OR groups and
 * `{ nonEmpty: true }` matches values that are not null, undefined, or blank
 * strings (`{ nonEmpty: false }` matches the opposite: missing or blank).
 *
 * Unknown configs keep an open string key set for generic consumers; inferred
 * block configs are restricted to their declared leaf paths.
 */
export type VisibleWhenValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | { readonly nonEmpty: boolean };

type VisibleWhenPath<Config> = unknown extends Config ? string : DotPath<Config>;

type VisibleWhenPropCondition<Config> = {
  readonly prop: VisibleWhenPath<Config>;
  readonly nonEmpty: true;
};

export type VisibleWhen<Config = unknown> =
  | { readonly [Key in VisibleWhenPath<Config>]?: VisibleWhenValue }
  | VisibleWhenPropCondition<Config>
  | { readonly anyOf: readonly VisibleWhen<Config>[] };

export type DashboardLayoutItem<Config = unknown> =
  | DotPath<Config>
  | (string & {})
  | {
      prop: DotPath<Config> | (string & {});
      label?: string;
      help?: string;
      visibleWhen?: VisibleWhen<Config>;
      /**
       * Inspector display width (CSS), e.g. `'50%'` to place two props on one row.
       * Defaults to full width when omitted.
       */
      width?: string;
    }
  | {
      descriptorControlField: DotPath<Config> | (string & {});
      label?: string;
      help?: string;
      visibleWhen?: VisibleWhen<Config>;
      /** Inspector display width (CSS), e.g. `'50%'`. */
      width?: string;
    };

/* -------------------------------------------------------------------------- */
/* propertyValue / descriptorControlField factories                           */
/* -------------------------------------------------------------------------- */

export function propertyValue<
  Type extends DoodFieldType,
  Value = import('./descriptor').FieldValueByType<Type>,
>(
  descriptor: import('./property-value').PropertyValueDescriptor<Type, Value>,
): PropertyValue<Type>;
export function propertyValue<
  Type extends DoodFieldType,
  Value = import('./descriptor').FieldValueByType<Type>,
>(
  descriptor: {
    type: Type;
    description?: string;
    options?: Record<string, unknown>;
    allowedRoles?: readonly string[];
    dashboardVisibilityWhen?: import('./property-value').PropertyValueDashboardVisibilityPredicate;
  },
  conditions: readonly ConditionalPropertyValue<Value | ParamRef | CasesValue>[],
): PropertyValue<Type>;
export function propertyValue(descriptor: any, conditions?: any): PropertyValue<any> {
  if (conditions !== undefined) {
    return {
      [PROPERTY_VALUE_BRAND]: true as const,
      kind: 'conditional',
      type: descriptor.type,
      conditions,
      description: descriptor.description,
      options: descriptor.options,
      allowedRoles: descriptor.allowedRoles,
      dashboardVisibilityWhen: descriptor.dashboardVisibilityWhen,
    } as unknown as PropertyValue<any>;
  }
  return {
    [PROPERTY_VALUE_BRAND]: true as const,
    kind: 'static',
    type: descriptor.type,
    value: descriptor.value,
    description: descriptor.description,
    options: descriptor.options,
    allowedRoles: descriptor.allowedRoles,
    dashboardVisibilityWhen: descriptor.dashboardVisibilityWhen,
  } as unknown as PropertyValue<any>;
}

export function descriptorControlField<Type extends DoodFieldType>(
  descriptor: {
    type: Type;
    value?: import('./descriptor').FieldValueByType<Type>;
    description?: string;
    options?: Record<string, unknown>;
    dashboardVisibilityWhen?: import('./property-value').PropertyValueDashboardVisibilityPredicate;
  },
): DescriptorControlField<Type> {
  return {
    [DESCRIPTOR_CONTROL_FIELD_BRAND]: true as const,
    type: descriptor.type,
    value: descriptor.value,
    description: descriptor.description,
    options: descriptor.options,
    dashboardVisibilityWhen: descriptor.dashboardVisibilityWhen,
  } as unknown as DescriptorControlField<Type>;
}

export function isPropertyValue(
  node: unknown,
): node is PropertyValue<DoodFieldType> {
  return (
    typeof node === 'object' &&
    node !== null &&
    (node as any)[PROPERTY_VALUE_BRAND] === true
  );
}

export function isDescriptorControlField(
  node: unknown,
): node is DescriptorControlField<DoodFieldType> {
  return (
    typeof node === 'object' &&
    node !== null &&
    (node as any)[DESCRIPTOR_CONTROL_FIELD_BRAND] === true
  );
}

/* -------------------------------------------------------------------------- */
/* Runtime resolution                                                         */
/* -------------------------------------------------------------------------- */

export function evaluateBlockCondition(
  condition: BlockCondition,
  context: import('./property-value').BlockContext,
): boolean {
  const opts = condition.options as Record<string, unknown>;
  switch (condition.type) {
    case 'locale':
      return typeof context.locale === 'string' && (opts.in as readonly string[]).includes(context.locale);
    case 'device':
      return typeof context.device === 'string' && (opts.in as readonly string[]).includes(context.device);
    case 'userAuth':
      return context.userAuth === (opts as { visibility: string }).visibility;
    case 'timeOfDay': {
      if (!context.timeOfDay) return false;
      return (
        context.timeOfDay.now >= (opts as { start: string }).start &&
        context.timeOfDay.now < (opts as { end: string }).end
      );
    }
    case 'weekday': {
      if (!context.weekday) return false;
      return (opts as { selectedDays: readonly number[] }).selectedDays.includes(
        context.weekday.now.getDay(),
      );
    }
    case 'dateRange': {
      if (!context.dateRange) return false;
      const now = context.dateRange.now.toISOString();
      return now >= (opts as { start: string }).start && now < (opts as { end: string }).end;
    }
    case 'journeyParameter': {
      if (!context.journeyParameters) return false;
      const key = (opts as { paramKey: string }).paramKey;
      const actual = context.journeyParameters[key];
      const operator = (opts as { operator: string }).operator;
      switch (operator) {
        case 'eq':
        case 'equals':
          return actual === (opts as { value?: unknown }).value;
        case 'neq':
        case 'notEquals':
          return actual !== (opts as { value?: unknown }).value;
        case 'defined':
        case 'exists':
          return actual != null && actual !== '';
        case 'notDefined':
          return actual == null || actual === '';
        case 'isTrue':
          return actual === true;
        case 'isFalse':
          return actual === false;
        case 'truthy':
          return Boolean(actual);
        case 'falsy':
          return !Boolean(actual);
        default:
          return false;
      }
    }
    case 'loyaltyMember':
      return context.isLoyaltyMember === Boolean(opts.value ?? true);
    case 'userTag': {
      const label = typeof opts.label === 'string' ? opts.label : '';
      const hasTag =
        label.length > 0 &&
        Array.isArray(context.userTags) &&
        context.userTags.includes(label);
      return opts.condition === 'hasNot' ? !hasTag : hasTag;
    }
    case 'testMode': {
      const inTestMode = context.isTestMode === true;
      return opts.visibility === 'notInTestMode' ? !inTestMode : inTestMode;
    }
    case 'cartType':
      return context.cartType === (opts as { value?: string }).value;
    default:
      return false;
  }
}

export function resolvePropertyValue<Type extends DoodFieldType>(
  property: PropertyValue<Type>,
  context: import('./property-value').BlockContext,
): import('./descriptor').FieldValueByType<Type> {
  if (property.kind === 'static' || property.kind === undefined) {
    return property.value as import('./descriptor').FieldValueByType<Type>;
  }
  if (property.kind === 'conditional' && property.conditions) {
    for (const candidate of property.conditions) {
      if (evaluateBlockCondition(candidate.condition, context)) {
        return candidate.value as import('./descriptor').FieldValueByType<Type>;
      }
    }
  }
  return undefined as import('./descriptor').FieldValueByType<Type>;
}

/* -------------------------------------------------------------------------- */
/* Descriptor + CB2 projection                                                */
/* -------------------------------------------------------------------------- */

type FieldOptionsForType<Type extends DoodFieldType> =
  Type extends 'text'
    ? import('./descriptor').TextFieldOptions
    : Type extends 'link'
      ? import('./descriptor').LinkFieldOptions
      : Type extends 'checkbox'
        ? import('./descriptor').CheckboxFieldOptions
        : Type extends 'color'
          ? import('./descriptor').ColorFieldOptions
          : Type extends 'image_file'
            ? import('./descriptor').ImageFileFieldOptions
            : Record<string, unknown>;

function buildFieldOptions<Type extends DoodFieldType>(
  _type: Type,
  path: string,
  options: Record<string, unknown> | undefined,
): FieldOptionsForType<Type> {
  const label =
    options && typeof options.label === 'string' ? options.label : path;
  const merged = { ...(options ?? {}), label } as Record<string, unknown>;
  // CB2 stores select/radio items under `choices`; CB1 templates read nested `options`.
  if (
    (_type === 'select' || _type === 'radio') &&
    Array.isArray(merged['choices']) &&
    !Array.isArray(merged['options'])
  ) {
    merged['options'] = merged['choices'];
  }
  return merged as FieldOptionsForType<Type>;
}

function toCb1FieldType<Type extends DoodFieldType>(type: Type): DoodFieldType {
  switch (type) {
    case 'string':
    case 'url':
      return 'text';
    case 'link':
      return 'link';
    case 'image':
      return 'image_file';
    case 'slider':
      return 'number';
    case 'unit_value':
      return 'text';
    default:
      return type;
  }
}

export function collectConfigFields(
  node: unknown,
  path: string,
  out: LegacyBlockField[],
): void {
  if (isPropertyValue(node)) {
    const fieldOptions = node.options as Record<string, unknown> | undefined;
    const handle = resolveLegacyFieldHandle(path, fieldOptions, false);
    if (node.type === 'spacing_box') {
      out.push(expandSpacingBoxToGroupedFields(handle, node));
      return;
    }
    const cb1Type = toCb1FieldType(node.type);
    const options = buildFieldOptions(cb1Type, path, fieldOptions as any) as Record<string, unknown>;
    backfillLegacyFieldDefault(cb1Type, node.value, options);
    out.push({
      handle,
      type: cb1Type,
      options,
      ...legacyAllowedRoles(node.allowedRoles),
    } as DoodBlockField);
    return;
  }
  if (isDescriptorControlField(node)) {
    return;
  }
  if (node && typeof node === 'object') {
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      collectConfigFields(child, path ? `${path}.${key}` : key, out);
    }
  }
}

export type LegacyDescriptorBuildInput = Omit<IBlockDescriptor, 'fields'> & {
  slots?: readonly SlotDefinition[];
  allowedIn?: readonly BlocAllowedIn[];
};

/** CB2 `allowedIn.bloc` uses glob `*`; CB1 `allowed_in` is a full-string regex. */
function toLegacyAllowedInPattern(bloc: string): string {
  return bloc.replace(/Component$/, '').replace(/\*/g, '.*');
}

/** CB1 picker keys omit the `Component` suffix (`MosaicCardBlockComponent` → `MosaicCardBlock`). */
function toLegacyAllowedBlockType(type: string): string {
  return type.replace(/Component$/, '');
}

export function buildLegacyDescriptor<Config>(
  metadata: LegacyDescriptorBuildInput,
  config: Config,
): IBlockDescriptor {
  const { slots, allowedIn, ...rest } = metadata;
  const fields: LegacyBlockField[] = [];
  collectConfigFields(config, '', fields);

  for (const slot of slots ?? []) {
    const allowedBlockTypes =
      slot.accept === 'all' || !slot.allowedTypes || slot.allowedTypes.length === 0
        ? ['.*']
        : slot.allowedTypes.map(toLegacyAllowedBlockType);
    fields.push({
      handle: toSnakeHandle(slot.key),
      type: 'block_collection',
      options: {
        label: typeof slot.name === 'string' ? slot.name : slot.key,
        allowed_block_types: allowedBlockTypes,
        ...(slot.max === undefined ? {} : { max: slot.max }),
        ...(slot.defaultBlocks?.length
          ? { default_blocks: slot.defaultBlocks.map(defaultBlock => defaultBlock.id) }
          : {}),
      },
      ...legacyAllowedRoles(slot.allowedRoles),
    } as DoodBlockField);
  }

  const descriptor: IBlockDescriptor = { ...rest, fields };
  // Prefer explicit legacy `allowed_in`. Otherwise project CB2 `allowedIn`
  // parents to legacy names (ShopPageComponent → ShopPage).
  if (!descriptor.allowed_in?.length && allowedIn?.length) {
    descriptor.allowed_in = [
      ...new Set(allowedIn.map(entry => toLegacyAllowedInPattern(entry.bloc))),
    ];
  }
  return descriptor;
}

const LEGACY_TO_CB2_TYPE: {
  [K in DoodFieldType]?: Cb2PropertyTypeFor<K>;
} = {
  text: 'string' as const,
  string: 'string' as const,
  textarea: 'textarea' as const,
  translation: 'string' as const,
  wysiwyg: 'wysiwyg' as const,
  url: 'url' as const,
  link: 'link' as const,
  select: 'select' as const,
  radio: 'radio' as const,
  checkbox: 'checkbox' as const,
  number: 'number' as const,
  slider: 'number' as const,
  color: 'color' as const,
  image: 'image' as const,
  image_file: 'image' as const,
  video_file: 'video' as const,
  spacing_box: 'spacing_box' as const,
  unit_value: 'unit_value' as const,
  suggestions_set_picker: 'suggestions_set' as const,
};

function toCb2PropertyType<Type extends DoodFieldType>(
  type: Type,
): import('./bloc-definition').BlocPropertyType {
  return (LEGACY_TO_CB2_TYPE[type] ?? type) as import('./bloc-definition').BlocPropertyType;
}

function conditionToCases(
  conditions: readonly ConditionalPropertyValue<unknown>[],
  fallback: SerializableValue,
  propertyType?: DoodFieldType,
): CasesValue | null {
  if (conditions.length === 0) return null;
  const first = conditions[0];
  if (!first) return null;
  const branches = conditions.map(branch => ({
    when: { ...branch.condition.options, $kind: branch.condition.type },
    value: toPropertySerializable(propertyType, branch.value),
  }));
  return { $cases: { dimension: first.condition.type, branches, default: fallback } };
}

function toPropertySerializable(
  propertyType: DoodFieldType | undefined,
  value: unknown,
): SerializableValue {
  if (propertyType === 'color') {
    const paletteParamRef = paletteColorValueToParamRef(value);
    if (paletteParamRef) {
      return paletteParamRef;
    }
  }

  return toSerializable(value);
}

function toSerializable(value: unknown): SerializableValue {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'object') {
    if ('$param' in (value as Record<string, unknown>)) {
      return { $param: (value as ParamRef).$param } as ParamRef;
    }
    if ('$cases' in (value as Record<string, unknown>)) {
      return value as CasesValue;
    }
    if (isPropertyValue(value)) {
      return propertyValueToSerializable(value as PropertyValue<DoodFieldType>);
    }
    if (isTranslationField(value)) {
      return translationToCases(value);
    }
    return JSON.parse(JSON.stringify(value)) as SerializableValue;
  }
  return JSON.parse(JSON.stringify(value)) as SerializableValue;
}

const CONTENT_BUILDER_COLOR_TYPES = new Set(['hex', 'palette', 'default']);

function isContentBuilderColorField(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  const type = obj['type'];
  if (typeof type !== 'string' || !CONTENT_BUILDER_COLOR_TYPES.has(type)) {
    return false;
  }
  const colorValue = obj['value'];
  return (
    typeof colorValue === 'string' || colorValue === null || colorValue === undefined
  );
}

function isTranslationField(value: unknown): value is TranslationField {
  if (typeof value !== 'object' || value === null) return false;
  if (isContentBuilderColorField(value)) return false;
  const entries = Object.entries(value as Record<string, unknown>);
  return entries.length > 0 && entries.every(([, v]) => typeof v === 'string');
}

function translationToCases(translation: TranslationField): CasesValue {
  const entries = Object.entries(translation);
  const branches = entries.map(([locale, text]) => ({
    when: { in: [locale] },
    value: text as SerializableValue,
  }));
  const fallback = (entries[0]?.[1] ?? '') as SerializableValue;
  return { $cases: { dimension: 'locale', branches, default: fallback } };
}

function propertyValueToSerializable(
  property: PropertyValue<DoodFieldType>,
): SerializableValue {
  if (property.kind === 'static') {
    return toPropertySerializable(property.type, property.value);
  }
  if (property.kind === 'conditional' && property.conditions) {
    const cases = conditionToCases(
      property.conditions as readonly ConditionalPropertyValue<unknown>[],
      null,
      property.type,
    );
    return cases ?? null;
  }
  return null;
}

function stripLabel(
  options: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!options) return undefined;
  const { label: _label, ...rest } = options;
  return Object.keys(rest).length === 0 ? undefined : rest;
}

function pushProperty(
  groups: Map<string, BlocProperty[]>,
  sectionId: string,
  property: BlocProperty,
): void {
  const list = groups.get(sectionId);
  if (list) {
    list.push(property);
  } else {
    groups.set(sectionId, [property]);
  }
}

export function collectConfigPropertyGroups(
  node: unknown,
  path: string,
  groups: Map<string, BlocProperty[]>,
): void {
  if (isPropertyValue(node)) {
    const sectionId = path.includes('.')
      ? toSnakeHandle(path.slice(0, path.lastIndexOf('.'))) || 'general'
      : 'general';
    const key = toSnakeHandle(lastSegment(path));
    const label =
      (node.options && typeof node.options.label === 'string'
        ? node.options.label
        : key) as import('./bloc-definition').I18nLabel;
    const property: BlocProperty = {
      key,
      type: toCb2PropertyType(node.type),
      label,
      options: stripLabel(node.options),
    };
    if (node.description) property.description = node.description;
    if (node.kind === 'static' && node.value !== undefined) {
      property.default = toPropertySerializable(node.type, node.value);
    } else if (node.kind === 'conditional' && node.conditions) {
      property.default =
        conditionToCases(
          node.conditions as readonly ConditionalPropertyValue<unknown>[],
          null,
          node.type,
        ) ?? undefined;
    }
    pushProperty(groups, sectionId, property);
    return;
  }
  if (isDescriptorControlField(node)) {
    return;
  }
  if (node && typeof node === 'object') {
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      collectConfigPropertyGroups(child, path ? `${path}.${key}` : key, groups);
    }
  }
}

export type EntityDefinitionProjectionInput = {
  componentType: string;
  name: import('./bloc-definition').I18nLabel;
  category: BlocCategory;
  description?: string;
  keywords?: readonly string[];
  slots?: readonly SlotDefinition[];
  isRootBlock?: true;
  isModalRoot?: true;
  hideFromPicker?: true;
  allowedIn?: readonly BlocAllowedIn[];
  isContentModal?: true;
  defaultHandle?: string;
};

export function buildPropertyGroupsFromConfig<Config>(
  config: Config,
): readonly BlocPropertyGroup[] {
  const groups = new Map<string, BlocProperty[]>();
  collectConfigPropertyGroups(config, '', groups);
  const propertyGroups: BlocPropertyGroup[] = [];
  for (const [sectionId, properties] of groups) {
    propertyGroups.push({ sectionId, label: sectionId, properties });
  }
  return propertyGroups;
}

function extractStaticValue(property: PropertyValue<DoodFieldType>): unknown {
  if (property.kind === 'static') {
    return property.value;
  }
  return undefined;
}

export function buildResolvedConfig<Config>(config: Config): ResolvedConfig<Config> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config as Record<string, unknown>)) {
    if (isPropertyValue(value)) {
      out[key] = extractStaticValue(value as PropertyValue<DoodFieldType>);
      continue;
    }
    if (isDescriptorControlField(value)) {
      continue;
    }
    if (value && typeof value === 'object') {
      out[key] = buildResolvedConfig(value);
    } else {
      out[key] = value;
    }
  }
  return out as ResolvedConfig<Config>;
}

export function buildOverrideConfig<Config>(config: Config): OverrideConfig<Config> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config as Record<string, unknown>)) {
    if (isPropertyValue(value) || isDescriptorControlField(value)) {
      continue;
    }
    if (value && typeof value === 'object') {
      out[key] = buildOverrideConfig(value);
    }
  }
  return out as OverrideConfig<Config>;
}

function collectLeafPaths(node: unknown, path: string, out: string[]): void {
  if (isPropertyValue(node) || isDescriptorControlField(node)) {
    out.push(path);
    return;
  }
  if (node && typeof node === 'object') {
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      collectLeafPaths(child, path ? `${path}.${key}` : key, out);
    }
  }
}

function buildDefaultDashboardLayout<Config>(
  config: Config,
  omitFields: readonly string[] | undefined,
): DashboardLayout<Config> {
  const leafPaths: string[] = [];
  collectLeafPaths(config, '', leafPaths);
  const omit = new Set(omitFields ?? []);
  const kept = leafPaths.filter(p => !omit.has(p));
  if (kept.length === 0) {
    return { omitFields, tabs: [] };
  }
  return {
    omitFields,
    tabs: [
      {
        label: 'Design',
        fieldsets: [
          {
            legend: 'Properties',
            fields: kept as unknown as readonly DashboardLayoutItem<Config>[],
          },
        ],
      },
    ],
  };
}

export function resolveDashboardLayout<Config>(
  layout: DashboardLayoutFactoryInput<Config>,
  config: Config,
): DashboardLayout<Config> {
  if (layout === 'displayAll') {
    return buildDefaultDashboardLayout(config, undefined);
  }
  if (layout.tabs) return layout;
  return buildDefaultDashboardLayout(config, layout.omitFields);
}

/* -------------------------------------------------------------------------- */
/* Config composition                                                         */
/* -------------------------------------------------------------------------- */

type ObservableLike<T> = { subscribe(observer: (value: T) => void): unknown };

export type ObservableValue<T> = T extends ObservableLike<infer Value> ? Value : never;

export type ScenarioContentOf<TDataSource extends { vm$: ObservableLike<unknown> }> =
  ObservableValue<TDataSource['vm$']>;

export type DoodBlockScenarioContext = {
  /** Dashboard layout tab key currently edited in CB2 Studio. */
  activeTab?: string | null;
  /** Live bloc/modal props used to refine scenario auto-selection. */
  props?: Record<string, unknown>;
};

export type DoodBlockScenarioFactory<TContent = unknown> = (
  ctx: DoodBlockScenarioContext,
) => DoodBlockScenarioMatrix<TContent>;

export type DoodBlockScenario<TContent = unknown> = {
  name: string;
  _devDescription: string;
  when?: string;
  default?: boolean;
  content?: Partial<TContent>;
  includes?: readonly DoodBlockScenario<TContent>[];
  /** When set, scenario is auto-selected when this predicate returns true. */
  activeWhen?: () => boolean;
};

export type ScenarioDescription<TContent = unknown> = Omit<
  DoodBlockScenario<TContent>,
  'name'
>;

export type ScenarioBuilder = {
  describe: <TContent = unknown>(
    name: string,
    scenario: ScenarioDescription<TContent>,
  ) => DoodBlockScenario<TContent>;
};

export const scenarioBuilder: ScenarioBuilder = {
  describe: (name, scenario) => ({ name, ...scenario }),
};

export {
  definitionSetupScenarios,
  EMPTY_SCENARIO_FACTORY,
} from './definition-setup-scenarios';
export type { DefinitionSetupScenariosFn } from './definition-setup-scenarios';

export function buildSharedAccessors<SharedFields extends SharedFieldsDefinition>(
  sharedFields: SharedFields,
): SharedFieldAccessors<SharedFields> {
  return {
    props: buildAccessorTree(sharedFields.props ?? []),
    descriptorControlFields: buildAccessorTree(sharedFields.descriptorControlFields ?? []),
  } as SharedFieldAccessors<SharedFields>;
}

function buildAccessorTree(
  fields: readonly SharedFieldDefinition[],
): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const { name, type } of fields) {
    const segments = name.split('.');
    let cursor: Record<string, unknown> = root;
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]!;
      if (i === segments.length - 1) {
        cursor[segment] = type;
      } else {
        const next = cursor[segment];
        cursor[segment] =
          next && typeof next === 'object' ? next : ({} as Record<string, unknown>);
        cursor = cursor[segment] as Record<string, unknown>;
      }
    }
  }
  return root;
}

export function componentTypeToPrefix(componentType: string): string {
  const base = componentType.endsWith('Component')
    ? componentType.slice(0, -'Component'.length)
    : componentType;
  return base.charAt(0).toLowerCase() + base.slice(1);
}

export function componentTypeToComposeConfigExportName(componentType: string): string {
  let base = componentType.endsWith('Component')
    ? componentType.slice(0, -'Component'.length)
    : componentType;
  if (base.endsWith('Atom')) {
    base = base.slice(0, -'Atom'.length);
  } else if (base.endsWith('Block')) {
    base = base.slice(0, -'Block'.length);
  }
  const camelBase = base.charAt(0).toLowerCase() + base.slice(1);
  return `compose${camelBase.charAt(0).toUpperCase()}${camelBase.slice(1)}Config`;
}

export function buildPrefixedExports<Config>(
  componentType: string,
  definition: {
    blockDescriptor?: IBlockDescriptor;
    modalDescriptor?: IBlockDescriptor;
    blocDefinition?: unknown;
    modalDefinition?: unknown;
    config: ResolvedConfig<Config>;
    properties: Config;
    dashboardLayout: DashboardLayout<Config>;
    scenarios: DoodBlockScenarioFactory;
    resolveConfig: (...args: never[]) => ResolvedConfig<Config>;
  },
  composeConfig: unknown,
  kind: 'block' | 'modal',
): Record<string, unknown> {
  const prefix = componentTypeToPrefix(componentType);
  const descriptorKey = kind === 'block' ? 'blockDescriptor' : 'modalDescriptor';
  const definitionKey = kind === 'block' ? 'blocDefinition' : 'modalDefinition';
  const definitionSuffix = kind === 'block' ? 'BlocDefinition' : 'ModalDefinition';
  return {
    [`${prefix}Descriptor`]: definition[descriptorKey],
    [`${prefix}${definitionSuffix}`]: definition[definitionKey],
    [`${prefix}Config`]: definition.config,
    [componentTypeToComposeConfigExportName(componentType)]: composeConfig,
    [`${prefix}Properties`]: definition.properties,
    [`${prefix}DashboardLayout`]: definition.dashboardLayout,
    [`${prefix}Scenarios`]: definition.scenarios,
    [`${prefix}DefinitionSetupScenarios`]: definitionSetupScenarios(componentType),
    [`${prefix}ResolveConfig`]: definition.resolveConfig,
  };
}
